import React, { useEffect, useState } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { companyUseCase } from "../../../di/container";
import { useParams } from "react-router-dom";
import { Button } from "../../components/UI/Button";
import { type FieldErrors, useForm } from "react-hook-form";
import TextInput from "../../components/UI/TextInput";
import { useNotifications } from "../../hooks/use-notifications";
import NotificationsList from "../../components/UI/NotificationList";

interface TossPaymentFormData {
  tossPayKey: string;
  tossSecretKey: string;
}

export const TossPayments: React.FC = () => {
  const { companyId } = useParams();
  const [hasTossSecretKey, setHasTossSecretKey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    notifications,
    addItem: addNotificationItem,
  } = useNotifications();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TossPaymentFormData>();

  useEffect(() => {
    (async () => {
      if (!companyId) return;

      setIsLoading(true);
      try {
        const companyInfo = await companyUseCase.getCompanyInfo({
          companyId: Number(companyId),
        });
        setValue("tossPayKey", companyInfo.tossPayKey ?? "");
        setHasTossSecretKey(companyInfo.hasTossSecretKey ?? false);
      } catch (error) {
        console.error("회사 정보 조회 실패:", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [companyId, setValue]);

  const onSubmit = async (data: TossPaymentFormData) => {
    if (!companyId) return;

    try {
      // 기존 회사 정보 조회
      const existingCompanyInfo = await companyUseCase.getCompanyInfo({
        companyId: Number(companyId),
      });

      // Client Key 업데이트 (회사 정보와 함께)
      await companyUseCase.updateCompanyInfo({
        address: existingCompanyInfo.address ?? "",
        addressDetail: existingCompanyInfo.addressDetail ?? "",
        businessCategory: existingCompanyInfo.businessCategory ?? "",
        businessNumber: existingCompanyInfo.businessNumber ?? "",
        businessType: existingCompanyInfo.businessType ?? "",
        ceoName: existingCompanyInfo.ceoName ?? "",
        phone: existingCompanyInfo.phone ?? "",
        secondaryContactType: existingCompanyInfo.secondaryContactType ?? "",
        secondaryContact: existingCompanyInfo.secondaryContact ?? "",
        companyName: existingCompanyInfo.companyName,
        domain: existingCompanyInfo.domain ?? "",
        zipCode: existingCompanyInfo.zipCode ?? "",
        companyId: existingCompanyInfo.id,
        companyAdminId: existingCompanyInfo.companyAdminId,
        homeTaxKey: existingCompanyInfo.homeTaxKey ?? "",
        tossPayKey: data.tossPayKey,
        channelTalkKey: existingCompanyInfo.channelTalkKey ?? "",
        ecommerceBizNumber: existingCompanyInfo.ecommerceBizNumber ?? "",
        businessHours: existingCompanyInfo.businessHours ?? "",
        lunchBreak: existingCompanyInfo.lunchBreak ?? "",
        businessHoursNotice: existingCompanyInfo.businessHoursNotice ?? "",
      });

      // Secret Key가 입력된 경우 별도 API로 저장
      if (data.tossSecretKey && data.tossSecretKey.trim()) {
        await companyUseCase.updateTossSecretKey({
          companyId: Number(companyId),
          tossSecretKey: data.tossSecretKey,
        });
        setHasTossSecretKey(true);
        setValue("tossSecretKey", "");
      }

      addNotificationItem("토스페이먼츠 설정이 저장되었습니다.");
    } catch (error) {
      console.error("저장 실패:", error);
      addNotificationItem("저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const onError = (errors: FieldErrors<TossPaymentFormData>) => {
    const keys = Object.keys(errors) as (keyof TossPaymentFormData)[];
    for (const key of keys) {
      const error = errors[key];
      if (error) {
        console.error(error);
        break;
      }
    }
  };

  if (isLoading) {
    return (
      <CompanyAdminLayout>
        <div className="p-5 flex items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </CompanyAdminLayout>
    );
  }

  return (
    <>
      <NotificationsList notifications={notifications} />
      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] flex flex-col items-start gap-[0.5rem]">
            <div className="flex flex-row justify-between w-full">
              <h2 className="text-[1.375rem] font-semibold mb-3 text-left">
                토스페이먼츠 설정
              </h2>
            </div>

            <form
              className="w-full max-w-[45rem]"
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              <div className="bg-[#EBF3FF] rounded-[8px] p-4 mb-6">
                <p className="text-[13px] text-[#4285F4]">
                  토스페이먼츠 연동을 위한 API Key를 설정합니다.
                  <br />
                  토스페이먼츠 개발자 센터에서 발급받은 키를 입력해주세요.
                </p>
              </div>

              <div className="text-start flex flex-col gap-[1.25rem] text-[14px]">
                <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] items-center">
                  <div className="font-medium md:mb-7">Client Key</div>
                  <div>
                    <TextInput
                      innerClassName="bg-gray-50"
                      error={{
                        message: errors.tossPayKey?.message || "",
                        isIconVisible: false,
                        isMessageVisible: true,
                      }}
                      variant="admin"
                      {...register("tossPayKey", {
                        required: false,
                      })}
                      placeholder="test_ck_ 또는 live_ck_ 로 시작하는 키"
                    />
                    <p className="text-[12px] text-[#888] mt-2">
                      프론트엔드에서 결제창 호출 시 사용됩니다.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[160px,auto] gap-[16px] items-center">
                  <div className="font-medium md:mb-7">Secret Key</div>
                  <div className="flex flex-col gap-2">
                    <TextInput
                      innerClassName="bg-gray-50"
                      error={{
                        message: errors.tossSecretKey?.message || "",
                        isIconVisible: false,
                        isMessageVisible: true,
                      }}
                      variant="admin"
                      type="password"
                      {...register("tossSecretKey", {
                        required: false,
                      })}
                      placeholder={
                        hasTossSecretKey
                          ? "●●●●●●●● (변경하려면 새 키 입력)"
                          : "test_sk_ 또는 live_sk_ 로 시작하는 키"
                      }
                    />
                    {hasTossSecretKey ? (
                      <p className="text-[12px] text-[#4285F4]">
                        Secret Key가 설정되어 있습니다. 변경하려면 새 키를 입력하세요.
                      </p>
                    ) : (
                      <p className="text-[12px] text-[#888]">
                        서버에서 결제 승인 시 사용됩니다. 암호화되어 저장됩니다.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="border border-[#D4D4D4] w-full my-8"></div>

              <div className="flex justify-end">
                <Button type="submit" variant="primary">
                  저장
                </Button>
              </div>
            </form>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};
